import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePortalAllowlistRealtime } from '../../hooks/usePortalAllowlistRealtime';
import { isValidEmail, normalizeEmail } from '../../lib/emailUtils';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import { isElevatedDepartmentSlug } from '../../lib/portalUserProfiles';
import {
  createUnifiedPortalUser,
  fetchUnifiedPortalUsers,
  NO_PORTAL_ACCESS,
  canRemoveUnifiedPortalUser,
  canRestoreUnifiedPortalUser,
  removeUnifiedPortalUser,
  restoreUnifiedPortalUser,
  setUnifiedPortalUserDepartment,
  setUnifiedPortalUserSalespersonCodes,
  setUnifiedPortalUserRole,
  type PortalAccessRole,
  type UnifiedPortalUser,
} from '../../lib/portalUnifiedUsers';
import { fetchApplicationFormDepartments } from '../../lib/applicationFormWorkflow';
import type { ApplicationFormDepartment } from '../../types/applicationForm';
import type { AllowlistPortalKind } from '../../lib/portalAllowlist';
import {
  assignableAllowlistRoleOptions,
  assignableFormsPortalRoleOptions,
  canAddAllowlistWithRole,
  canAddFormsPortalWithRole,
  canAssignAllowlistRole,
  canAssignFormsPortalRole,
  canDeleteAllowedEmail,
  canEditAllowedEmail,
  canEditFormsPortalRole,
  canManageAllowedEmails,
  normalizeUserRole,
  type FormsPortalRole,
} from '../../lib/settingsPermissions';
import type { UserRole } from '../../types/crm';
import {
  fetchSalespersonEmails,
  type SalespersonEmailRow,
} from '../../lib/applicationFormSettings';
import {
  fetchActiveTicketAgentEmails,
  setTicketAgentByEmail,
} from '../../lib/tickets';
import {
  fetchPricingUpdateRoles,
  setPricingUpdateRoleEnabled,
} from '../../lib/pricingUpdateRoles';
import {
  fetchCertificateRequestRoles,
  setCertificateRequestRoleEnabled,
} from '../../lib/certificateRequestRoles';
import {
  fetchAccountCreationRoles,
  setAccountCreationRoleEnabled,
} from '../../lib/accountCreationRoles';
import { Modal } from '../ui/Modal';
import { ApplicationFormDepartmentsPanel } from '../forms/settings/ApplicationFormDepartmentsPanel';
import { ApplicationFormSalespersonPanel } from '../forms/settings/ApplicationFormSalespersonPanel';

type UsersMatrixTab = 'portal' | 'forms';

type FormsMatrixKind = 'account-creation' | 'certificate-request' | 'pricing-update';

type FormsMatrixFlags = {
  left: boolean; // Submission or Verifier
  right: boolean; // Approver
};

type FormsMatrixMap = Record<string, Record<FormsMatrixKind, FormsMatrixFlags>>;

const FORMS_MATRIX_COLUMNS: {
  kind: FormsMatrixKind;
  label: string;
  leftLabel: string;
  rightLabel: string;
}[] = [
  {
    kind: 'account-creation',
    label: 'Account Creation',
    leftLabel: 'Verifier',
    rightLabel: 'Approver',
  },
  {
    kind: 'certificate-request',
    label: 'Certificate Request',
    leftLabel: 'Verifier',
    rightLabel: 'Approver',
  },
  {
    kind: 'pricing-update',
    label: 'Price Update',
    leftLabel: 'Verifier',
    rightLabel: 'Approver',
  },
];

function emptyFormsFlags(): Record<FormsMatrixKind, FormsMatrixFlags> {
  return {
    'account-creation': { left: false, right: false },
    'certificate-request': { left: false, right: false },
    'pricing-update': { left: false, right: false },
  };
}

function emptyFormsMatrixMap(): FormsMatrixMap {
  return {};
}

const PORTAL_COLUMNS: { kind: AllowlistPortalKind; label: string; short: string }[] = [
  { kind: 'crm', label: 'Customer Relationship Management', short: 'CRM' },
  { kind: 'it', label: 'IT Management', short: 'IT' },
  { kind: 'dem', label: 'Data Entry Management', short: 'DEM' },
  { kind: 'forms', label: 'Application Form', short: 'Forms' },
  { kind: 'report', label: 'Report', short: 'Rpt' },
  { kind: 'accounts', label: 'Accounts', short: 'Acc' },
  { kind: 'purchasing', label: 'Purchasing', short: 'Pur' },
  { kind: 'design', label: 'Design', short: 'Des' },
];

const UNASSIGNED_DEPT_KEY = '__unassigned__';

type EditDraft = {
  departmentId: string;
  salespersonCodes: string[];
  roles: Record<AllowlistPortalKind, PortalAccessRole>;
  ticketing: boolean;
};

function emptyRolesDraft(): Record<AllowlistPortalKind, PortalAccessRole> {
  return {
    crm: NO_PORTAL_ACCESS,
    it: NO_PORTAL_ACCESS,
    dem: NO_PORTAL_ACCESS,
    forms: NO_PORTAL_ACCESS,
    report: NO_PORTAL_ACCESS,
    accounts: NO_PORTAL_ACCESS,
    purchasing: NO_PORTAL_ACCESS,
    design: NO_PORTAL_ACCESS,
  };
}

function roleCellLabel(role: PortalAccessRole): string {
  if (role === NO_PORTAL_ACCESS) return 'N/A';
  const normalized = normalizeUserRole(String(role));
  if (normalized === 'admin') return 'Admin';
  if (normalized === 'it') return 'IT';
  if (normalized === 'management') return 'Management';
  return 'User';
}

function sameStringList(a: string[], b: string[]): boolean {
  const left = [...new Set(a.map((v) => v.trim()).filter(Boolean))].sort();
  const right = [...new Set(b.map((v) => v.trim()).filter(Boolean))].sort();
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

/** True when every active salesperson code is selected (show as "All" in the matrix). */
function hasAllSalesCodes(selected: string[], allCodes: string[]): boolean {
  if (allCodes.length === 0) return false;
  const set = new Set(selected.map((c) => c.trim()).filter(Boolean));
  return allCodes.every((code) => set.has(code));
}

/** Portal role helpers. */
function crmRoleOptions(actorRole: UserRole) {
  return [
    { value: NO_PORTAL_ACCESS, label: 'No Access' },
    ...assignableAllowlistRoleOptions(actorRole),
  ];
}
function formsRoleOptions(actorRole: UserRole) {
  return [
    { value: NO_PORTAL_ACCESS, label: 'No Access' },
    ...assignableFormsPortalRoleOptions(actorRole),
  ];
}
function slotForKind(user: UnifiedPortalUser, kind: AllowlistPortalKind) {
  if (kind === 'crm') return user.crm;
  if (kind === 'it') return user.it;
  if (kind === 'dem') return user.dem;
  if (kind === 'report') return user.report;
  if (kind === 'accounts') return user.accounts;
  if (kind === 'purchasing') return user.purchasing;
  if (kind === 'design') return user.design;
  return user.forms;
}
function canSetCrmRole(
  actorRole: UserRole,
  currentRole: PortalAccessRole,
  nextRole: PortalAccessRole,
): boolean {
  if (nextRole === NO_PORTAL_ACCESS) {
    if (currentRole === NO_PORTAL_ACCESS) return false;
    return canDeleteAllowedEmail(actorRole, normalizeUserRole(currentRole as UserRole));
  }
  if (currentRole === NO_PORTAL_ACCESS) {
    return canAddAllowlistWithRole(actorRole, nextRole as UserRole);
  }
  return canAssignAllowlistRole(
    actorRole,
    normalizeUserRole(currentRole as UserRole),
    nextRole as UserRole,
  );
}
function canSetFormsRole(
  actorRole: UserRole,
  currentRole: PortalAccessRole,
  nextRole: PortalAccessRole,
): boolean {
  const current =
    currentRole === NO_PORTAL_ACCESS ? 'none' : (currentRole as FormsPortalRole);
  const next = nextRole === NO_PORTAL_ACCESS ? 'none' : (nextRole as FormsPortalRole);
  return canAssignFormsPortalRole(actorRole, current, next);
}

function userDepartmentId(user: UnifiedPortalUser): string {
  return user.departmentId ?? user.forms.department_id ?? '';
}

type UnifiedPortalUsersPanelProps = {
  actorRole?: UserRole;
  /** When set (e.g. from Settings sidebar), controls the matrix view and hides in-page tabs. */
  matrixTab?: UsersMatrixTab;
};

export function UnifiedPortalUsersPanel({
  actorRole: actorRoleProp,
  matrixTab,
}: UnifiedPortalUsersPanelProps) {
  const { role: authRole, authUser } = useAuth();
  const actorRole = actorRoleProp ?? authRole;
  const canManage = canManageAllowedEmails(actorRole);
  const [rows, setRows] = useState<UnifiedPortalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [usersTabInternal, setUsersTabInternal] = useState<UsersMatrixTab>('portal');
  const usersTab = matrixTab ?? usersTabInternal;
  const showInPageTabs = matrixTab == null;
  const [formsMatrix, setFormsMatrix] = useState<FormsMatrixMap>(emptyFormsMatrixMap);
  const [formsEditOpen, setFormsEditOpen] = useState(false);
  const [formsEditDraft, setFormsEditDraft] = useState<Record<FormsMatrixKind, FormsMatrixFlags>>(
    () => emptyFormsFlags(),
  );
  const [formsEditBaseline, setFormsEditBaseline] = useState<
    Record<FormsMatrixKind, FormsMatrixFlags>
  >(() => emptyFormsFlags());
  const [formsEditSaving, setFormsEditSaving] = useState(false);
  const [departmentsOpen, setDepartmentsOpen] = useState(false);
  const [salespersonOpen, setSalespersonOpen] = useState(false);
  const [showRemovedUsers, setShowRemovedUsers] = useState(false);
  const [salespersonRows, setSalespersonRows] = useState<SalespersonEmailRow[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newCrm, setNewCrm] = useState<PortalAccessRole>(NO_PORTAL_ACCESS);
  const [newIt, setNewIt] = useState<PortalAccessRole>(NO_PORTAL_ACCESS);
  const [newDem, setNewDem] = useState<PortalAccessRole>(NO_PORTAL_ACCESS);
  const [newForms, setNewForms] = useState<PortalAccessRole>(NO_PORTAL_ACCESS);
  const [newReport, setNewReport] = useState<PortalAccessRole>(NO_PORTAL_ACCESS);
  const [newAccounts, setNewAccounts] = useState<PortalAccessRole>(NO_PORTAL_ACCESS);
  const [newPurchasing, setNewPurchasing] = useState<PortalAccessRole>(NO_PORTAL_ACCESS);
  const [newDesign, setNewDesign] = useState<PortalAccessRole>(NO_PORTAL_ACCESS);
  const [newDepartmentId, setNewDepartmentId] = useState('');
  const [newSalespersonCodes, setNewSalespersonCodes] = useState<string[]>([]);
  const [departments, setDepartments] = useState<ApplicationFormDepartment[]>([]);
  const [departmentDrafts, setDepartmentDrafts] = useState<Record<string, string>>({});
  const [salespersonDrafts, setSalespersonDrafts] = useState<Record<string, string[]>>({});
  const [ticketingEmails, setTicketingEmails] = useState<Set<string>>(() => new Set());
  const [newTicketing, setNewTicketing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [salesPickerOpen, setSalesPickerOpen] = useState(false);
  const savingKeyRef = useRef<string | null>(null);
  savingKeyRef.current = savingKey;
  const crmSelectOptions = useMemo(() => crmRoleOptions(actorRole), [actorRole]);
  const formsSelectOptions = useMemo(() => formsRoleOptions(actorRole), [actorRole]);
  /** Role choices when a portal checkbox is on (No Access = uncheck). */
  const portalRoleChoices = useMemo(
    () => crmSelectOptions.filter((option) => option.value !== NO_PORTAL_ACCESS),
    [crmSelectOptions],
  );
  const formsRoleChoices = useMemo(
    () => formsSelectOptions.filter((option) => option.value !== NO_PORTAL_ACCESS),
    [formsSelectOptions],
  );
  const selectedUser = useMemo(
    () => rows.find((row) => normalizeEmail(row.email) === normalizeEmail(selectedEmail ?? '')) ?? null,
    [rows, selectedEmail],
  );

  function buildEditDraft(user: UnifiedPortalUser): EditDraft {
    const deptId = userDepartmentId(user);
    if (isElevatedDeptId(deptId)) {
      const roles = emptyRolesDraft();
      for (const col of PORTAL_COLUMNS) roles[col.kind] = 'admin';
      const allCodes = salespersonRows
        .filter((row) => row.active)
        .map((row) => row.code);
      return {
        departmentId: deptId,
        salespersonCodes: allCodes.length > 0 ? allCodes : [...(user.salespersonCodes ?? [])],
        roles,
        ticketing: true,
      };
    }
    const roles = emptyRolesDraft();
    for (const col of PORTAL_COLUMNS) {
      roles[col.kind] = slotForKind(user, col.kind).role;
    }
    return {
      departmentId: deptId,
      salespersonCodes: [...(user.salespersonCodes ?? [])],
      roles,
      ticketing: ticketingEmails.has(normalizeEmail(user.email)),
    };
  }

  function openEditUser(user: UnifiedPortalUser) {
    setError(null);
    setSalesPickerOpen(false);
    setFormsEditOpen(false);
    setEditDraft(buildEditDraft(user));
    setSelectedEmail(user.email);
  }

  function closeEditUser() {
    setSelectedEmail(null);
    setEditDraft(null);
    setSalesPickerOpen(false);
    setFormsEditOpen(false);
  }

  function openFormsEditUser(user: UnifiedPortalUser) {
    setError(null);
    setEditDraft(null);
    setSelectedEmail(user.email);
    const flags = formsFlagsFor(user.email);
    setFormsEditDraft({
      'account-creation': { ...flags['account-creation'] },
      'certificate-request': { ...flags['certificate-request'] },
      'pricing-update': { ...flags['pricing-update'] },
    });
    setFormsEditBaseline({
      'account-creation': { ...flags['account-creation'] },
      'certificate-request': { ...flags['certificate-request'] },
      'pricing-update': { ...flags['pricing-update'] },
    });
    setFormsEditOpen(true);
  }

  function closeFormsEditUser() {
    if (formsEditSaving) return;
    setFormsEditOpen(false);
    setSelectedEmail(null);
    setError(null);
  }

  async function handleFormsEditDone() {
    if (!selectedUser) return;
    setFormsEditSaving(true);
    setError(null);
    const actor = authUser?.email ?? null;
    try {
      const tasks: Promise<void>[] = [];
      for (const col of FORMS_MATRIX_COLUMNS) {
        const next = formsEditDraft[col.kind];
        const prev = formsEditBaseline[col.kind];
        if (next.left !== prev.left) {
          if (col.kind === 'account-creation') {
            tasks.push(
              setAccountCreationRoleEnabled(selectedUser.email, 'verifier', next.left, actor),
            );
          } else if (col.kind === 'certificate-request') {
            tasks.push(
              setCertificateRequestRoleEnabled(
                selectedUser.email,
                'authorised',
                next.left,
                actor,
              ),
            );
          } else {
            tasks.push(
              setPricingUpdateRoleEnabled(selectedUser.email, 'authorised', next.left, actor),
            );
          }
        }
        if (next.right !== prev.right) {
          if (col.kind === 'account-creation') {
            tasks.push(
              setAccountCreationRoleEnabled(selectedUser.email, 'approver', next.right, actor),
            );
          } else if (col.kind === 'certificate-request') {
            tasks.push(
              setCertificateRequestRoleEnabled(selectedUser.email, 'approver', next.right, actor),
            );
          } else {
            tasks.push(
              setPricingUpdateRoleEnabled(selectedUser.email, 'approver', next.right, actor),
            );
          }
        }
      }
      await Promise.all(tasks);
      const emailKey = normalizeEmail(selectedUser.email);
      setFormsMatrix((prev) => ({
        ...prev,
        [emailKey]: {
          'account-creation': { ...formsEditDraft['account-creation'] },
          'certificate-request': { ...formsEditDraft['certificate-request'] },
          'pricing-update': { ...formsEditDraft['pricing-update'] },
        },
      }));
      setFormsEditBaseline({
        'account-creation': { ...formsEditDraft['account-creation'] },
        'certificate-request': { ...formsEditDraft['certificate-request'] },
        'pricing-update': { ...formsEditDraft['pricing-update'] },
      });
      setFormsEditOpen(false);
      setSelectedEmail(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save form roles.');
    } finally {
      setFormsEditSaving(false);
    }
  }

  function departmentSlug(departmentId: string | null | undefined): string | null {
    if (!departmentId) return null;
    return departments.find((dept) => dept.id === departmentId)?.slug ?? null;
  }

  function isElevatedDeptId(departmentId: string | null | undefined): boolean {
    return isElevatedDepartmentSlug(departmentSlug(departmentId));
  }

  const createElevatedByDepartment = isElevatedDeptId(newDepartmentId);
  const editElevatedByDepartment = isElevatedDeptId(
    editDraft?.departmentId ?? selectedUser?.departmentId,
  );

  const salespersonOptions = useMemo(() => {
    return salespersonRows
      .filter((row) => row.active)
      .map((row) => ({
        value: row.code,
        label: row.code,
      }));
  }, [salespersonRows]);

  const activeRows = useMemo(() => rows.filter((row) => !row.removedAt), [rows]);
  const removedRows = useMemo(() => rows.filter((row) => Boolean(row.removedAt)), [rows]);

  const departmentGroups = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; sortOrder: number; users: UnifiedPortalUser[] }>();
    for (const dept of departments) {
      byId.set(dept.id, {
        id: dept.id,
        name: dept.name,
        sortOrder: dept.sort_order ?? 0,
        users: [],
      });
    }
    const unassigned: UnifiedPortalUser[] = [];
    for (const user of activeRows) {
      const deptId = userDepartmentId(user);
      const group = deptId ? byId.get(deptId) : undefined;
      if (group) group.users.push(user);
      else unassigned.push(user);
    }
    const groups = [...byId.values()]
      .filter((g) => g.users.length > 0)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    for (const g of groups) {
      g.users.sort((a, b) =>
        (a.displayName || a.email).localeCompare(b.displayName || b.email, undefined, {
          sensitivity: 'base',
        }),
      );
    }
    if (unassigned.length > 0) {
      unassigned.sort((a, b) =>
        (a.displayName || a.email).localeCompare(b.displayName || b.email, undefined, {
          sensitivity: 'base',
        }),
      );
      groups.push({
        id: UNASSIGNED_DEPT_KEY,
        name: 'Unassigned',
        sortOrder: 9999,
        users: unassigned,
      });
    }
    return groups;
  }, [activeRows, departments]);

  /** Application Form tab: Forms portal access, or elevated dept (IT/Management — always treated as Forms admin in Portal). */
  const formsDepartmentGroups = useMemo(() => {
    return departmentGroups
      .map((group) => ({
        ...group,
        users: group.users.filter((user) => {
          if (isElevatedDeptId(userDepartmentId(user))) return true;
          return slotForKind(user, 'forms').role !== NO_PORTAL_ACCESS;
        }),
      }))
      .filter((group) => group.users.length > 0);
  }, [departmentGroups, departments]);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [list, agents, acRoles, ctrRoles, pufRoles] = await Promise.all([
        fetchUnifiedPortalUsers({ includeRemoved: true }),
        fetchActiveTicketAgentEmails().catch(() => new Set<string>()),
        fetchAccountCreationRoles().catch(() => []),
        fetchCertificateRequestRoles().catch(() => []),
        fetchPricingUpdateRoles().catch(() => []),
      ]);
      setRows(list);
      setTicketingEmails(agents);
      setNameDrafts(Object.fromEntries(list.map((row) => [row.email, row.displayName])));
      setDepartmentDrafts(
        Object.fromEntries(
          list.map((row) => [
            row.email,
            row.departmentId ?? row.forms.department_id ?? departments[0]?.id ?? '',
          ]),
        ),
      );
      setSalespersonDrafts(
        Object.fromEntries(list.map((row) => [row.email, [...row.salespersonCodes]])),
      );

      const nextMatrix: FormsMatrixMap = {};
      for (const user of list) {
        nextMatrix[normalizeEmail(user.email)] = emptyFormsFlags();
      }
      for (const row of acRoles) {
        const key = normalizeEmail(row.email);
        if (!nextMatrix[key]) nextMatrix[key] = emptyFormsFlags();
        if (row.role === 'verifier' || String(row.role) === 'submission') {
          nextMatrix[key]['account-creation'].left = true;
        }
        if (row.role === 'approver') nextMatrix[key]['account-creation'].right = true;
      }
      for (const row of ctrRoles) {
        const key = normalizeEmail(row.email);
        if (!nextMatrix[key]) nextMatrix[key] = emptyFormsFlags();
        if (row.role === 'authorised') nextMatrix[key]['certificate-request'].left = true;
        if (row.role === 'approver') nextMatrix[key]['certificate-request'].right = true;
      }
      for (const row of pufRoles) {
        const key = normalizeEmail(row.email);
        if (!nextMatrix[key]) nextMatrix[key] = emptyFormsFlags();
        if (row.role === 'authorised') nextMatrix[key]['pricing-update'].left = true;
        if (row.role === 'approver') nextMatrix[key]['pricing-update'].right = true;
      }
      setFormsMatrix(nextMatrix);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users.');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [departments]);

  function patchUser(
    email: string,
    patch: (user: UnifiedPortalUser) => UnifiedPortalUser,
  ) {
    const key = normalizeEmail(email);
    setRows((prev) => prev.map((row) => (normalizeEmail(row.email) === key ? patch(row) : row)));
  }

  useEffect(() => {
    if (!canManage) return;
    void fetchApplicationFormDepartments()
      .then(setDepartments)
      .catch(() => setDepartments([]));
  }, [canManage]);

  const reloadDepartments = useCallback(() => {
    void fetchApplicationFormDepartments()
      .then(setDepartments)
      .catch(() => setDepartments([]));
  }, []);

  const reloadSalespersonCodes = useCallback(() => {
    void fetchSalespersonEmails()
      .then(setSalespersonRows)
      .catch(() => setSalespersonRows([]));
  }, []);

  useEffect(() => {
    if (!canManage) return;
    void fetchSalespersonEmails()
      .then(setSalespersonRows)
      .catch(() => setSalespersonRows([]));
  }, [canManage]);

  useEffect(() => {
    if (!canManage) return;
    void load();
  }, [canManage, load]);

  usePortalAllowlistRealtime({
    enabled: canManage,
    onChange: () => {
      if (savingKeyRef.current) return;
      void load({ silent: true });
    },
  });

  function resetCreateForm() {
    setNewEmail('');
    setNewDisplayName('');
    setNewCrm(NO_PORTAL_ACCESS);
    setNewIt(NO_PORTAL_ACCESS);
    setNewDem(NO_PORTAL_ACCESS);
    setNewForms(NO_PORTAL_ACCESS);
    setNewReport(NO_PORTAL_ACCESS);
    setNewAccounts(NO_PORTAL_ACCESS);
    setNewPurchasing(NO_PORTAL_ACCESS);
    setNewDesign(NO_PORTAL_ACCESS);
    setNewTicketing(false);
    setNewDepartmentId('');
    setNewSalespersonCodes([]);
  }

  function openCreateModal() {
    setError(null);
    resetCreateForm();
    setCreateOpen(true);
  }

  function closeCreateModal() {
    if (adding) return;
    setCreateOpen(false);
    resetCreateForm();
  }

  async function handleRoleChange(
    user: UnifiedPortalUser,
    kind: AllowlistPortalKind,
    nextRole: PortalAccessRole,
  ) {
    const slot = slotForKind(user, kind);
    if (slot.role === nextRole) return;
    const allowed =
      kind === 'forms'
        ? canSetFormsRole(actorRole, slot.role, nextRole)
        : canSetCrmRole(actorRole, slot.role, nextRole);
    if (!allowed) {
      setError('You cannot assign this role.');
      return;
    }
    const key = `${user.email}:${kind}`;
    setSavingKey(key);
    setError(null);
    try {
      const displayName = nameDrafts[user.email]?.trim() || user.displayName;
      const formsDept =
        kind === 'forms'
          ? departmentDrafts[user.email] || user.departmentId || departments[0]?.id
          : undefined;
      await setUnifiedPortalUserRole(kind, user.email, displayName, nextRole, slot.id, formsDept);
      patchUser(user.email, (row) => {
        const nextSlot = {
          ...slotForKind(row, kind),
          role: nextRole,
          display_name: displayName || null,
          ...(kind === 'forms' && formsDept ? { department_id: formsDept } : {}),
          ...(nextRole === NO_PORTAL_ACCESS ? { id: undefined } : {}),
        };
        if (kind === 'crm') return { ...row, displayName, crm: nextSlot };
        if (kind === 'it') return { ...row, displayName, it: nextSlot };
        if (kind === 'dem') return { ...row, displayName, dem: nextSlot };
        if (kind === 'report') return { ...row, displayName, report: nextSlot };
        if (kind === 'accounts') return { ...row, displayName, accounts: nextSlot };
        if (kind === 'purchasing') return { ...row, displayName, purchasing: nextSlot };
        if (kind === 'design') return { ...row, displayName, design: nextSlot };
        return { ...row, displayName, forms: nextSlot };
      });
      if (!slot.id && nextRole !== NO_PORTAL_ACCESS) {
        await load({ silent: true });
      }
      if (kind === 'crm' && nextRole === NO_PORTAL_ACCESS) {
        await setTicketAgentByEmail(user.email, false);
        setTicketingEmails((prev) => {
          const next = new Set(prev);
          next.delete(normalizeEmail(user.email));
          return next;
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : formatSupabaseError(e) || 'Failed to update access.');
    } finally {
      setSavingKey(null);
    }
  }

  async function handleTicketingChange(user: UnifiedPortalUser, enabled: boolean) {
    const key = normalizeEmail(user.email);
    setSavingKey(`${user.email}:ticketing`);
    setError(null);
    try {
      await setTicketAgentByEmail(
        user.email,
        enabled,
        nameDrafts[user.email]?.trim() || user.displayName || '',
      );
      setTicketingEmails((prev) => {
        const next = new Set(prev);
        if (enabled) next.add(key);
        else next.delete(key);
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update ticketing access.');
    } finally {
      setSavingKey(null);
    }
  }

  async function handleSalespersonCodesChange(user: UnifiedPortalUser, nextCodes: string[]) {
    const departmentId =
      departmentDrafts[user.email] ?? user.departmentId ?? user.forms.department_id ?? '';
    if (!departmentId) {
      setError('Select a department before assigning salesperson codes.');
      return;
    }
    const next = [...new Set(nextCodes.map((c) => c.trim()).filter(Boolean))];
    const current = user.salespersonCodes ?? [];
    if (sameStringList(next, current)) return;
    const key = `${user.email}:salesperson`;
    setSavingKey(key);
    setError(null);
    try {
      const displayName = nameDrafts[user.email]?.trim() || user.displayName;
      await setUnifiedPortalUserSalespersonCodes(user.email, next, displayName, departmentId);
      setSalespersonDrafts((prev) => ({ ...prev, [user.email]: next }));
      patchUser(user.email, (row) => ({ ...row, salespersonCodes: next }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update salesperson codes.');
      setSalespersonDrafts((prev) => ({ ...prev, [user.email]: [...current] }));
    } finally {
      setSavingKey(null);
    }
  }

  function toggleSalespersonCode(
    user: UnifiedPortalUser | null,
    code: string,
    mode: 'create' | 'edit',
  ) {
    if (mode === 'create') {
      setNewSalespersonCodes((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
      );
      return;
    }
    if (!user) return;
    const current = salespersonDrafts[user.email] ?? user.salespersonCodes ?? [];
    const next = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];
    setSalespersonDrafts((prev) => ({ ...prev, [user.email]: next }));
    void handleSalespersonCodesChange(user, next);
  }

  function renderSalespersonCodePicker(
    mode: 'create' | 'edit',
    user?: UnifiedPortalUser,
  ) {
    const selected =
      mode === 'create'
        ? newSalespersonCodes
        : salespersonDrafts[user?.email ?? ''] ?? user?.salespersonCodes ?? [];
    const disabled =
      mode === 'edit' &&
      user != null &&
      savingKey === `${user.email}:salesperson`;

    return (
      <div className="unified-users-salesperson">
        <div className="unified-users-visible-grid unified-users-salesperson__grid">
          {salespersonOptions.map((opt) => (
            <label key={opt.value} className="unified-users-visible-option">
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                disabled={disabled}
                onChange={() => toggleSalespersonCode(user ?? null, opt.value, mode)}
              />
              <span>
                <span className="unified-users-visible-option__name">{opt.value}</span>
                <span className="unified-users-visible-option__email">{opt.label}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  function renderSalespersonCodeSection(mode: 'create' | 'edit', user?: UnifiedPortalUser) {
    const selected =
      mode === 'create'
        ? newSalespersonCodes
        : salespersonDrafts[user?.email ?? ''] ?? user?.salespersonCodes ?? [];
    const summary =
      selected.length > 0 ? `Salesperson code (${selected.length})` : 'Salesperson code';

    return (
      <details className="unified-users-salesperson-details">
        <summary className="unified-users-salesperson-details__summary">{summary}</summary>
        {renderSalespersonCodePicker(mode, user)}
      </details>
    );
  }

  async function handleCreateAccount(e: React.FormEvent) {
    e.preventDefault();
    const email = normalizeEmail(newEmail);
    if (!isValidEmail(email)) {
      setError('Enter a valid email address.');
      return;
    }
    const portalRoles = [newCrm, newIt, newDem, newReport, newAccounts, newPurchasing, newDesign];
    const elevatedByDepartment = isElevatedDeptId(newDepartmentId);
    if (
      !elevatedByDepartment &&
      portalRoles.every((role) => role === NO_PORTAL_ACCESS) &&
      newForms === NO_PORTAL_ACCESS
    ) {
      setError('Grant access in at least one system.');
      return;
    }
    if (
      !portalRoles.every((role) => {
        if (role === NO_PORTAL_ACCESS) return true;
        return canAddAllowlistWithRole(actorRole, role as UserRole);
      })
    ) {
      setError('One or more roles cannot be assigned with your permissions.');
      return;
    }
    if (
      newForms !== NO_PORTAL_ACCESS &&
      !canAddFormsPortalWithRole(actorRole, newForms as FormsPortalRole)
    ) {
      setError('Application Form role cannot be assigned with your permissions.');
      return;
    }
    if (!newDepartmentId) {
      setError('Select a department for this person.');
      return;
    }
    setAdding(true);
    setError(null);
    try {
      const allSalesCodes = salespersonOptions.map((opt) => opt.value);
      await createUnifiedPortalUser({
        email,
        displayName: newDisplayName,
        crm: elevatedByDepartment ? 'admin' : newCrm,
        it: elevatedByDepartment ? 'admin' : newIt,
        dem: elevatedByDepartment ? 'admin' : newDem,
        forms: elevatedByDepartment ? 'admin' : newForms,
        report: elevatedByDepartment ? 'admin' : newReport,
        accounts: elevatedByDepartment ? 'admin' : newAccounts,
        purchasing: elevatedByDepartment ? 'admin' : newPurchasing,
        design: elevatedByDepartment ? 'admin' : newDesign,
        departmentId: newDepartmentId,
        salespersonCodes: elevatedByDepartment ? allSalesCodes : newSalespersonCodes,
        elevatedByDepartment,
      });
      if (
        elevatedByDepartment ||
        (newTicketing && newCrm !== NO_PORTAL_ACCESS)
      ) {
        await setTicketAgentByEmail(email, true, newDisplayName);
      }
      resetCreateForm();
      setCreateOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account.');
    } finally {
      setAdding(false);
    }
  }

  async function handleRemoveUser(user: UnifiedPortalUser) {
    if (!canRemoveUnifiedPortalUser(actorRole, user)) return;
    const isSelf =
      authUser?.email != null &&
      normalizeEmail(authUser.email) === normalizeEmail(user.email);
    if (isSelf) {
      setError('You cannot remove your own access.');
      return;
    }
    const reason = window.prompt(
      `Reason for moving ${user.email} to Removed users (required).\nAccounts and contacts are kept.`,
    );
    if (reason === null) return;
    if (!reason.trim()) {
      setError('A reason is required when removing a user.');
      return;
    }
    setSavingKey(`${user.email}:remove`);
    setError(null);
    try {
      await removeUnifiedPortalUser(user, reason);
      closeEditUser();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove user.');
    } finally {
      setSavingKey(null);
    }
  }

  async function handleRestoreUser(user: UnifiedPortalUser) {
    if (!canRestoreUnifiedPortalUser(actorRole, user)) return;
    if (!window.confirm(`Restore ${user.email}? Re-grant system access after restoring if needed.`)) {
      return;
    }
    setSavingKey(`${user.email}:restore`);
    setError(null);
    try {
      await restoreUnifiedPortalUser(user);
      closeEditUser();
      setShowRemovedUsers(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to restore user.');
    } finally {
      setSavingKey(null);
    }
  }

  async function writePortalRole(
    user: UnifiedPortalUser,
    kind: AllowlistPortalKind,
    next: PortalAccessRole,
    departmentId: string,
    force = false,
  ) {
    const prev = slotForKind(user, kind).role;
    if (!force && prev === next) return;
    await setUnifiedPortalUserRole(
      kind,
      user.email,
      user.displayName,
      next,
      slotForKind(user, kind).id,
      kind === 'forms' ? departmentId || undefined : undefined,
    );
  }

  async function handleEditDone() {
    if (!selectedUser || !editDraft) return;
    const user = selectedUser;
    const draft = editDraft;
    setEditSaving(true);
    setError(null);
    try {
      const wasElevated = isElevatedDeptId(userDepartmentId(user));
      const nowElevated = isElevatedDeptId(draft.departmentId);
      const deptId = draft.departmentId || userDepartmentId(user) || '';
      if (!deptId) throw new Error('Select a department.');

      if (draft.departmentId && draft.departmentId !== (user.departmentId ?? '')) {
        await setUnifiedPortalUserDepartment(
          user.email,
          draft.departmentId,
          user.displayName,
          user.forms.id,
        );
      }

      const allSalesCodes = salespersonOptions.map((opt) => opt.value);
      const hadTicket = ticketingEmails.has(normalizeEmail(user.email));

      if (nowElevated && !wasElevated) {
        // Entering IT/Management: overwrite every portal to admin + all sales codes in Supabase.
        for (const col of PORTAL_COLUMNS) {
          await writePortalRole(user, col.kind, 'admin', deptId, true);
        }
        await setUnifiedPortalUserSalespersonCodes(
          user.email,
          allSalesCodes,
          user.displayName,
          deptId,
        );
        if (!hadTicket) {
          await setTicketAgentByEmail(user.email, true, user.displayName || '');
        }
      } else if (!nowElevated && wasElevated) {
        // Leaving IT/Management: wipe stored portal roles / sales codes (do not restore prior).
        for (const col of PORTAL_COLUMNS) {
          await writePortalRole(user, col.kind, NO_PORTAL_ACCESS, deptId, true);
        }
        await setUnifiedPortalUserSalespersonCodes(user.email, [], user.displayName, deptId);
        if (hadTicket) {
          await setTicketAgentByEmail(user.email, false, user.displayName || '');
        }
      } else if (!nowElevated) {
        if (!sameStringList(draft.salespersonCodes, user.salespersonCodes ?? [])) {
          await setUnifiedPortalUserSalespersonCodes(
            user.email,
            draft.salespersonCodes,
            user.displayName,
            deptId,
          );
        }
        for (const col of PORTAL_COLUMNS) {
          const next = draft.roles[col.kind] ?? NO_PORTAL_ACCESS;
          await writePortalRole(user, col.kind, next, deptId);
        }
        const wantTicket =
          draft.roles.crm !== NO_PORTAL_ACCESS ? draft.ticketing : false;
        if (hadTicket !== wantTicket) {
          await setTicketAgentByEmail(user.email, wantTicket, user.displayName || '');
        }
      } else if (nowElevated && wasElevated) {
        // Staying elevated: keep DB aligned to full admin + all sales codes.
        for (const col of PORTAL_COLUMNS) {
          await writePortalRole(user, col.kind, 'admin', deptId, true);
        }
        await setUnifiedPortalUserSalespersonCodes(
          user.email,
          allSalesCodes,
          user.displayName,
          deptId,
        );
        if (!hadTicket) {
          await setTicketAgentByEmail(user.email, true, user.displayName || '');
        }
      }

      closeEditUser();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save changes.');
    } finally {
      setEditSaving(false);
    }
  }

  function renderMatrixPortalCell(user: UnifiedPortalUser, kind: AllowlistPortalKind) {
    const elevated = isElevatedDeptId(userDepartmentId(user));
    if (elevated) {
      return (
        <td key={kind} className="unified-users-matrix__portal is-on">
          <div className="unified-users-matrix__portal-inner">
            <span className="unified-users-matrix__check" aria-hidden>
              ☑
            </span>
            <span className="unified-users-matrix__role">Admin</span>
          </div>
        </td>
      );
    }
    const role = slotForKind(user, kind).role;
    const on = role !== NO_PORTAL_ACCESS;
    return (
      <td key={kind} className={`unified-users-matrix__portal${on ? ' is-on' : ''}`}>
        <div className="unified-users-matrix__portal-inner">
          <span className="unified-users-matrix__check" aria-hidden>
            {on ? '☑' : '☐'}
          </span>
          <span className="unified-users-matrix__role">{roleCellLabel(role)}</span>
        </div>
      </td>
    );
  }

  function formsFlagsFor(email: string): Record<FormsMatrixKind, FormsMatrixFlags> {
    return formsMatrix[normalizeEmail(email)] ?? emptyFormsFlags();
  }

  function renderFormsMatrixCell(user: UnifiedPortalUser, col: (typeof FORMS_MATRIX_COLUMNS)[number]) {
    const flags = formsFlagsFor(user.email)[col.kind];
    return (
      <td key={col.kind} className="unified-users-matrix__forms-cell">
        <div className="unified-users-matrix__forms-stack unified-users-matrix__forms-stack--row">
          <div
            className={`unified-users-matrix__forms-tick${flags.left ? ' is-on' : ''}`}
            aria-label={`${user.displayName || user.email} ${col.label} ${col.leftLabel}: ${
              flags.left ? 'on' : 'off'
            }`}
          >
            <span className="unified-users-matrix__check unified-users-matrix__check--inline" aria-hidden>
              {flags.left ? '☑' : '☐'}
            </span>
            <span>{col.leftLabel}</span>
          </div>
          <div
            className={`unified-users-matrix__forms-tick${flags.right ? ' is-on' : ''}`}
            aria-label={`${user.displayName || user.email} ${col.label} ${col.rightLabel}: ${
              flags.right ? 'on' : 'off'
            }`}
          >
            <span className="unified-users-matrix__check unified-users-matrix__check--inline" aria-hidden>
              {flags.right ? '☑' : '☐'}
            </span>
            <span>{col.rightLabel}</span>
          </div>
        </div>
      </td>
    );
  }

  function renderAccessFields(mode: 'create' | 'edit', user?: UnifiedPortalUser) {
    return PORTAL_COLUMNS.map((col) => {
      const isForms = col.kind === 'forms';
      const options = isForms ? formsSelectOptions : crmSelectOptions;

      if (mode === 'create') {
        const value =
          col.kind === 'crm'
            ? newCrm
            : col.kind === 'it'
              ? newIt
              : col.kind === 'dem'
                ? newDem
                : col.kind === 'report'
                  ? newReport
                  : col.kind === 'accounts'
                    ? newAccounts
                    : col.kind === 'purchasing'
                      ? newPurchasing
                      : col.kind === 'design'
                        ? newDesign
                      : newForms;
        const setValue =
          col.kind === 'crm'
            ? setNewCrm
            : col.kind === 'it'
              ? setNewIt
              : col.kind === 'dem'
                ? setNewDem
                : col.kind === 'report'
                  ? setNewReport
                  : col.kind === 'accounts'
                    ? setNewAccounts
                    : col.kind === 'purchasing'
                      ? setNewPurchasing
                      : col.kind === 'design'
                        ? setNewDesign
                      : setNewForms;
        return (
          <div key={col.kind} className="unified-users-access-field" title={col.label}>
            <label className="field">
              <span className="field-label">{col.short}</span>
              <select
                className="field-input"
                value={value}
                onChange={(e) => {
                  const next = e.target.value as PortalAccessRole;
                  setValue(next);
                  if (col.kind === 'crm' && next === NO_PORTAL_ACCESS) {
                    setNewTicketing(false);
                  }
                }}
              >
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {col.kind === 'crm' && (
              <label className="unified-users-ticketing-toggle" title="Allow Tickets tab in CRM">
                <input
                  type="checkbox"
                  checked={newTicketing}
                  disabled={newCrm === NO_PORTAL_ACCESS}
                  onChange={(e) => setNewTicketing(e.target.checked)}
                />
                <span>Ticketing</span>
              </label>
            )}
          </div>
        );
      }

      if (!user) return null;
      const slot = slotForKind(user, col.kind);
      const selectKey = `${user.email}:${col.kind}`;
      const canSet = isForms ? canSetFormsRole : canSetCrmRole;
      const editable =
        slot.role === NO_PORTAL_ACCESS
          ? options.some(
              (option) =>
                option.value !== NO_PORTAL_ACCESS &&
                (isForms
                  ? canAddFormsPortalWithRole(actorRole, option.value as FormsPortalRole)
                  : canAddAllowlistWithRole(actorRole, option.value as UserRole)),
            )
          : isForms
            ? canEditFormsPortalRole(actorRole, slot.role as FormsPortalRole)
            : canEditAllowedEmail(actorRole, normalizeUserRole(slot.role as UserRole));
      const ticketingOn = ticketingEmails.has(normalizeEmail(user.email));
      const elevatedAlways = isElevatedDeptId(userDepartmentId(user));

      return (
        <div key={col.kind} className="unified-users-access-field" title={col.label}>
          <label className="field">
            <span className="field-label">{col.short}</span>
            <select
              className={`field-input${
                slot.role === NO_PORTAL_ACCESS ? ' unified-users-role-select--none' : ''
              }`}
              value={slot.role}
              disabled={!editable || savingKey === selectKey}
              title={col.label}
              onChange={(e) =>
                void handleRoleChange(user, col.kind, e.target.value as PortalAccessRole)
              }
            >
              {options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.value !== slot.role && !canSet(actorRole, slot.role, option.value)}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {col.kind === 'crm' && (
            <label
              className="unified-users-ticketing-toggle"
              title={
                elevatedAlways
                  ? 'IT / Management always have Ticketing access'
                  : 'Allow Tickets tab in CRM'
              }
            >
              <input
                type="checkbox"
                checked={elevatedAlways || ticketingOn}
                disabled={
                  elevatedAlways ||
                  slot.role === NO_PORTAL_ACCESS ||
                  savingKey === `${user.email}:ticketing`
                }
                onChange={(e) => void handleTicketingChange(user, e.target.checked)}
              />
              <span>Ticketing</span>
            </label>
          )}
        </div>
      );
    });
  }

  if (!canManage) {
    return null;
  }

  return (
    <>
    <section className="card settings-panel unified-users-panel">
      <div className="settings-panel__head unified-users-panel__head">
        <div className="unified-users-panel__title-row">
          <h2 className="settings-panel__title">User management</h2>
          <div className="unified-users-panel__actions">
            <span className="settings-panel__count">{activeRows.length} people</span>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setDepartmentsOpen(true)}
            >
              Departments
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setSalespersonOpen(true)}
            >
              Salesperson
            </button>
            {removedRows.length > 0 ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setShowRemovedUsers(true)}
              >
                Removed users ({removedRows.length})
              </button>
            ) : null}
            <button type="button" className="btn-primary btn-sm" onClick={openCreateModal}>
              Create account
            </button>
          </div>
        </div>
      </div>
      {error && !createOpen ? <div className="alert alert-error">{error}</div> : null}

      {showInPageTabs ? (
        <div className="unified-users-panel__tabs" role="tablist" aria-label="User management views">
          <button
            type="button"
            role="tab"
            aria-selected={usersTab === 'portal'}
            className={`unified-users-panel__tab${usersTab === 'portal' ? ' is-active' : ''}`}
            onClick={() => setUsersTabInternal('portal')}
          >
            Portal
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={usersTab === 'forms'}
            className={`unified-users-panel__tab${usersTab === 'forms' ? ' is-active' : ''}`}
            onClick={() => setUsersTabInternal('forms')}
          >
            Application Form
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="settings-loading">
          <div className="empty-state__spinner" />
          <p>Loading users…</p>
        </div>
      ) : activeRows.length === 0 ? (
        <p className="settings-empty">No users yet. Create an account to begin.</p>
      ) : usersTab === 'portal' ? (
        <div className="unified-users-dept-list" role="tabpanel">
          {departmentGroups.map((group) => (
            <section key={group.id} className="unified-users-dept">
              <header className="unified-users-dept__head">
                <h3 className="unified-users-dept__title">{group.name}</h3>
                <span className="unified-users-dept__count">
                  {group.users.length} {group.users.length === 1 ? 'person' : 'people'}
                </span>
              </header>
              <div className="unified-users-matrix-wrap">
                <table className="unified-users-matrix">
                  <thead>
                    <tr>
                      <th scope="col" className="unified-users-matrix__user-col">
                        User
                      </th>
                      <th scope="col">Sales code</th>
                      {PORTAL_COLUMNS.map((col) => (
                        <th key={col.kind} scope="col" title={col.label}>
                          {col.short.toUpperCase()}
                        </th>
                      ))}
                      <th scope="col" className="unified-users-matrix__actions-col">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.users.map((user) => {
                      const codes = user.salespersonCodes ?? [];
                      const elevated = isElevatedDeptId(userDepartmentId(user));
                      const canRemove = canRemoveUnifiedPortalUser(actorRole, user);
                      return (
                        <tr key={user.email}>
                          <td className="unified-users-matrix__user">
                            <span className="unified-users-matrix__name">
                              {user.displayName || 'Unnamed'}
                            </span>
                            <span className="unified-users-matrix__email">{user.email}</span>
                          </td>
                          <td className="unified-users-matrix__sales">
                            {elevated ||
                            hasAllSalesCodes(
                              codes,
                              salespersonOptions.map((opt) => opt.value),
                            )
                              ? 'All'
                              : codes.length > 0
                                ? codes.join(', ')
                                : '—'}
                          </td>
                          {PORTAL_COLUMNS.map((col) => renderMatrixPortalCell(user, col.kind))}
                          <td className="unified-users-matrix__actions">
                            <button
                              type="button"
                              className="unified-users-matrix__icon-btn"
                              title="Edit"
                              aria-label={`Edit ${user.displayName || user.email}`}
                              onClick={() => openEditUser(user)}
                            >
                              ✎
                            </button>
                            {canRemove ? (
                              <button
                                type="button"
                                className="unified-users-matrix__icon-btn unified-users-matrix__icon-btn--danger"
                                title="Remove"
                                aria-label={`Remove ${user.displayName || user.email}`}
                                disabled={
                                  authUser?.email != null &&
                                  normalizeEmail(authUser.email) === normalizeEmail(user.email)
                                }
                                onClick={() => void handleRemoveUser(user)}
                              >
                                ×
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="unified-users-dept-list" role="tabpanel">
          {formsDepartmentGroups.length === 0 ? (
            <p className="settings-empty">
              No users with Application Form portal access yet. Grant Forms access on the Portal
              tab first.
            </p>
          ) : (
            formsDepartmentGroups.map((group) => (
              <section key={`forms-${group.id}`} className="unified-users-dept">
                <header className="unified-users-dept__head">
                  <h3 className="unified-users-dept__title">{group.name}</h3>
                  <span className="unified-users-dept__count">
                    {group.users.length} {group.users.length === 1 ? 'person' : 'people'}
                  </span>
                </header>
                <div className="unified-users-matrix-wrap">
                  <table className="unified-users-matrix unified-users-matrix--forms">
                    <thead>
                      <tr>
                        <th scope="col" className="unified-users-matrix__user-col">
                          User
                        </th>
                        {FORMS_MATRIX_COLUMNS.map((col) => (
                          <th key={col.kind} scope="col">
                            {col.label}
                          </th>
                        ))}
                        <th
                          scope="col"
                          className="unified-users-matrix__actions-col"
                          aria-label="Edit"
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {group.users.map((user) => (
                        <tr key={`forms-${user.email}`}>
                          <td className="unified-users-matrix__user">
                            <span className="unified-users-matrix__name">
                              {user.displayName || 'Unnamed'}
                            </span>
                            <span className="unified-users-matrix__email">{user.email}</span>
                          </td>
                          {FORMS_MATRIX_COLUMNS.map((col) => renderFormsMatrixCell(user, col))}
                          <td className="unified-users-matrix__actions">
                            <button
                              type="button"
                              className="unified-users-matrix__icon-btn"
                              title="Edit"
                              aria-label={`Edit form roles for ${user.displayName || user.email}`}
                              onMouseDown={(e) => {
                                if (e.button === 0) e.preventDefault();
                              }}
                              onClick={() => openFormsEditUser(user)}
                            >
                              ✎
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))
          )}
        </div>
      )}

      <Modal
        title="Create account"
        open={createOpen}
        onClose={closeCreateModal}
        wide
        panelClassName="modal-panel--unified-users"
      >
        <form className="unified-users-create" onSubmit={(e) => void handleCreateAccount(e)}>
          {error && createOpen ? <div className="alert alert-error">{error}</div> : null}
          <div className="unified-users-create__section">
            <p className="unified-users-create__section-title">Profile</p>
            <div className="unified-users-create__profile">
              <label className="field">
                <span className="field-label">Display name</span>
                <input
                  className="field-input"
                  value={newDisplayName}
                  onChange={(e) => setNewDisplayName(e.target.value)}
                  placeholder="Display name"
                  autoFocus
                />
              </label>
              <label className="field">
                <span className="field-label">Work email</span>
                <input
                  className="field-input"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="name@banteckhan.com.sg"
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">Department</span>
                <select
                  className="field-input"
                  value={newDepartmentId}
                  onChange={(e) => setNewDepartmentId(e.target.value)}
                  required
                >
                  <option value="">Select department</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {renderSalespersonCodeSection('create')}

          <hr className="unified-users-create__divider" />

          <div className="unified-users-create__section">
            <p className="unified-users-create__section-title">System access</p>
            {createElevatedByDepartment ? null : (
              <div className="unified-users-create__access">{renderAccessFields('create')}</div>
            )}
          </div>

          <div className="unified-users-create__footer">
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={closeCreateModal}
              disabled={adding}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary btn-sm" disabled={adding}>
              {adding ? 'Creating…' : 'Create account'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        title={
          selectedUser
            ? `Edit · ${selectedUser.displayName || selectedUser.email}`
            : 'Edit user'
        }
        open={selectedUser != null && editDraft != null}
        onClose={closeEditUser}
        wide
        panelClassName="modal-panel--unified-users"
      >
        {selectedUser && editDraft ? (
          <div className="unified-users-edit">
            {error && selectedEmail ? <div className="alert alert-error">{error}</div> : null}
            <div className="unified-users-edit__identity">
              <p className="unified-users-edit__name">
                {selectedUser.displayName || 'Unnamed'}
              </p>
              <p className="unified-users-edit__email">{selectedUser.email}</p>
            </div>

            <div className="unified-users-edit__top-row">
              <label className="field">
                <span className="field-label">Department</span>
                <select
                  className="field-input"
                  value={editDraft.departmentId}
                  disabled={editSaving}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    const nextElevated = isElevatedDeptId(nextId);
                    const wasElevated = isElevatedDeptId(editDraft.departmentId);
                    const allSalesCodes = salespersonOptions.map((opt) => opt.value);
                    setEditDraft((prev) => {
                      if (!prev) return prev;
                      if (nextElevated && !wasElevated) {
                        const roles = emptyRolesDraft();
                        for (const col of PORTAL_COLUMNS) roles[col.kind] = 'admin';
                        return {
                          ...prev,
                          departmentId: nextId,
                          roles,
                          salespersonCodes: allSalesCodes,
                          ticketing: true,
                        };
                      }
                      if (!nextElevated && wasElevated) {
                        return {
                          ...prev,
                          departmentId: nextId,
                          roles: emptyRolesDraft(),
                          salespersonCodes: [],
                          ticketing: false,
                        };
                      }
                      return { ...prev, departmentId: nextId };
                    });
                  }}
                >
                  <option value="" disabled>
                    Select department
                  </option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </label>

              <div className="field unified-users-edit__sales">
                <span className="field-label">Sales code</span>
                <button
                  type="button"
                  className="field-input unified-users-edit__sales-trigger"
                  disabled={editSaving}
                  onClick={() => setSalesPickerOpen((open) => !open)}
                >
                  {salespersonOptions.length > 0 &&
                  hasAllSalesCodes(
                    editDraft.salespersonCodes,
                    salespersonOptions.map((opt) => opt.value),
                  )
                    ? 'All'
                    : editDraft.salespersonCodes.length > 0
                      ? editDraft.salespersonCodes.join(', ')
                      : 'Select sales codes…'}
                </button>
                {salesPickerOpen ? (
                  <div className="unified-users-edit__sales-menu">
                    {salespersonOptions.length === 0 ? (
                      <p className="field-hint">No salesperson codes yet.</p>
                    ) : (
                      <>
                        <label className="unified-users-edit__sales-option unified-users-edit__sales-option--all">
                          <input
                            type="checkbox"
                            checked={hasAllSalesCodes(
                              editDraft.salespersonCodes,
                              salespersonOptions.map((opt) => opt.value),
                            )}
                            disabled={editSaving}
                            onChange={(e) => {
                              const allCodes = salespersonOptions.map((opt) => opt.value);
                              setEditDraft((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      salespersonCodes: e.target.checked ? allCodes : [],
                                    }
                                  : prev,
                              );
                            }}
                          />
                          <span>All</span>
                        </label>
                        {salespersonOptions.map((opt) => {
                          const checked = editDraft.salespersonCodes.includes(opt.value);
                          return (
                            <label key={opt.value} className="unified-users-edit__sales-option">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={editSaving}
                                onChange={() =>
                                  setEditDraft((prev) => {
                                    if (!prev) return prev;
                                    const next = checked
                                      ? prev.salespersonCodes.filter((c) => c !== opt.value)
                                      : [...prev.salespersonCodes, opt.value];
                                    return { ...prev, salespersonCodes: next };
                                  })
                                }
                              />
                              <span>{opt.value}</span>
                            </label>
                          );
                        })}
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="unified-users-edit__portals">
              <p className="unified-users-create__section-title">Portals</p>
              {editElevatedByDepartment ? (
                <p className="field-hint">
                  IT / Management gets all portals (Admin) and all sales codes. Leaving that
                  department clears stored portal access so old roles do not come back.
                </p>
              ) : (
                <div className="unified-users-edit__portal-list">
                  {PORTAL_COLUMNS.map((col) => {
                    const role = editDraft.roles[col.kind] ?? NO_PORTAL_ACCESS;
                    const on = role !== NO_PORTAL_ACCESS;
                    const choices = col.kind === 'forms' ? formsRoleChoices : portalRoleChoices;
                    return (
                      <div
                        key={col.kind}
                        className={`unified-users-edit__portal-row${on ? ' is-on' : ''}`}
                      >
                        <label className="unified-users-edit__portal-check">
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={editSaving}
                            onChange={(e) => {
                              const enabled = e.target.checked;
                              setEditDraft((prev) => {
                                if (!prev) return prev;
                                const nextRole = enabled
                                  ? ((choices[0]?.value as PortalAccessRole) ?? 'user')
                                  : NO_PORTAL_ACCESS;
                                const nextRoles = { ...prev.roles, [col.kind]: nextRole };
                                return {
                                  ...prev,
                                  roles: nextRoles,
                                  ticketing:
                                    col.kind === 'crm' && !enabled ? false : prev.ticketing,
                                };
                              });
                            }}
                          />
                          <span title={col.label}>{col.short}</span>
                        </label>
                        {on ? (
                          <div className="unified-users-edit__portal-controls">
                            <select
                              className="field-input unified-users-edit__role-select"
                              value={role}
                              disabled={editSaving}
                              aria-label={`${col.short} role`}
                              onChange={(e) => {
                                const next = e.target.value as PortalAccessRole;
                                setEditDraft((prev) =>
                                  prev
                                    ? { ...prev, roles: { ...prev.roles, [col.kind]: next } }
                                    : prev,
                                );
                              }}
                            >
                              {choices.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {col.kind === 'crm' ? (
                              <label className="unified-users-ticketing-toggle">
                                <input
                                  type="checkbox"
                                  checked={editDraft.ticketing}
                                  disabled={editSaving}
                                  onChange={(e) =>
                                    setEditDraft((prev) =>
                                      prev ? { ...prev, ticketing: e.target.checked } : prev,
                                    )
                                  }
                                />
                                <span>Ticketing</span>
                              </label>
                            ) : null}
                          </div>
                        ) : (
                          <span className="unified-users-edit__portal-off">Off</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="unified-users-create__footer">
              <div className="unified-users-edit__footer-left">
                {selectedUser.removedAt && canRestoreUnifiedPortalUser(actorRole, selectedUser) ? (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={editSaving || savingKey === `${selectedUser.email}:restore`}
                    onClick={() => void handleRestoreUser(selectedUser)}
                  >
                    Restore user
                  </button>
                ) : canRemoveUnifiedPortalUser(actorRole, selectedUser) ? (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={
                      editSaving ||
                      (authUser?.email != null &&
                        normalizeEmail(authUser.email) === normalizeEmail(selectedUser.email))
                    }
                    onClick={() => void handleRemoveUser(selectedUser)}
                  >
                    Remove user
                  </button>
                ) : null}
              </div>
              <div className="unified-users-edit__footer-right">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={editSaving}
                  onClick={closeEditUser}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  disabled={editSaving || !editDraft.departmentId || Boolean(selectedUser.removedAt)}
                  onClick={() => void handleEditDone()}
                >
                  {editSaving ? 'Saving…' : 'Done'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        title={
          selectedUser
            ? `Edit form roles · ${selectedUser.displayName || selectedUser.email}`
            : 'Edit form roles'
        }
        open={formsEditOpen && selectedUser != null && editDraft == null}
        onClose={closeFormsEditUser}
        panelClassName="modal-panel--unified-users"
      >
        {selectedUser && formsEditOpen ? (
          <div className="unified-users-approval">
            <p className="field-hint">
              Tick roles for this person, then Done. Same pattern as Portal access editing.
            </p>
            {error ? <div className="alert alert-error">{error}</div> : null}
            <div className="unified-users-approval__table-wrap">
              <table className="unified-users-approval__table">
                <thead>
                  <tr>
                    <th scope="col">Form</th>
                    <th scope="col">Verifier</th>
                    <th scope="col">Approver</th>
                  </tr>
                </thead>
                <tbody>
                  {FORMS_MATRIX_COLUMNS.map((col) => {
                    const flags = formsEditDraft[col.kind];
                    return (
                      <tr key={col.kind}>
                        <th scope="row">{col.label}</th>
                        <td>
                          <label className="unified-users-approval__check">
                            <input
                              type="checkbox"
                              checked={flags.left}
                              disabled={formsEditSaving}
                              aria-label={`${col.label} ${col.leftLabel}`}
                              onChange={(e) => {
                                const enabled = e.target.checked;
                                setFormsEditDraft((prev) => ({
                                  ...prev,
                                  [col.kind]: { ...prev[col.kind], left: enabled },
                                }));
                              }}
                            />
                            <span>{col.leftLabel}</span>
                          </label>
                        </td>
                        <td>
                          <label className="unified-users-approval__check">
                            <input
                              type="checkbox"
                              checked={flags.right}
                              disabled={formsEditSaving}
                              aria-label={`${col.label} ${col.rightLabel}`}
                              onChange={(e) => {
                                const enabled = e.target.checked;
                                setFormsEditDraft((prev) => ({
                                  ...prev,
                                  [col.kind]: { ...prev[col.kind], right: enabled },
                                }));
                              }}
                            />
                            <span>{col.rightLabel}</span>
                          </label>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="unified-users-create__footer">
              <span />
              <div className="unified-users-edit__footer-right">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={formsEditSaving}
                  onClick={closeFormsEditUser}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  disabled={formsEditSaving}
                  onClick={() => void handleFormsEditDone()}
                >
                  {formsEditSaving ? 'Saving…' : 'Done'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        title="Departments"
        open={departmentsOpen}
        onClose={() => setDepartmentsOpen(false)}
        wide
        panelClassName="modal-panel--unified-users"
      >
        <ApplicationFormDepartmentsPanel
          embedded
          onChanged={reloadDepartments}
        />
      </Modal>

      <Modal
        title="Salesperson codes"
        open={salespersonOpen}
        onClose={() => setSalespersonOpen(false)}
        wide
        panelClassName="modal-panel--unified-users"
      >
        <ApplicationFormSalespersonPanel
          embedded
          onChanged={reloadSalespersonCodes}
        />
      </Modal>

      <Modal
        title="Removed users"
        open={showRemovedUsers}
        onClose={() => setShowRemovedUsers(false)}
        wide
        panelClassName="modal-panel--unified-users"
      >
        <div className="unified-users-create">
          <p className="settings-panel__lead unified-users-dept-modal__lead">
            Portal access is revoked. Assigned accounts and contacts stay linked by salesperson
            code. Restore a person, then re-grant system access if needed.
          </p>
          {removedRows.length === 0 ? (
            <p className="settings-empty">No removed users.</p>
          ) : (
            <div className="unified-users-grid">
              {removedRows.map((user) => (
                <button
                  key={user.email}
                  type="button"
                  className="unified-users-card"
                  onClick={() => {
                    setShowRemovedUsers(false);
                    openEditUser(user);
                  }}
                >
                  <span className="unified-users-card__name">
                    {user.displayName || user.email}
                  </span>
                  <span className="unified-users-card__email">{user.email}</span>
                  {user.removedReason ? (
                    <span className="unified-users-card__email">{user.removedReason}</span>
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </section>
    </>
  );
}
